import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

import ReactSmartGallery from '../../vendor/react-smart-gallery';
import { imageUrl } from '../config';
import { useWebsite } from '../hooks/useWebsite';
import { ReactComponent as ArrowLeft } from '../icons/icon-arrow-left-circle.svg';
import { ReactComponent as ArrowRight } from '../icons/icon-arrow-right-circle.svg';
import { ReactComponent as IconClose } from '../icons/icon-close-circle.svg';
import { isClickAwayFromElement } from '../utils/dom';
import { isMobile } from '../utils/web';

// Host du CDN images GC (imageUrl). Une URL absolue d'un AUTRE host ne peut pas passer par l'optimiseur
// Next (`_next/image` → 400 « url parameter is not allowed », host hors `images.remotePatterns`). En mode
// presse, les images de source RÉEMPLOYÉES sont hotlinkées depuis des CDN presse → on les rend `unoptimized`
// (raw <img>, comme la page article MediaArticle). Les images GC-hébergées restent optimisées.
const GC_IMAGE_HOST = (() => {
  try {
    return new URL(imageUrl).host;
  } catch {
    return '';
  }
})();

const isExternalImageUrl = (url: string): boolean => {
  if (!url || !/^https?:\/\//i.test(url)) return false;
  try {
    return new URL(url).host !== GC_IMAGE_HOST;
  } catch {
    return false;
  }
};

const CustomGallery = ({ photos }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { graphdebate } = useWebsite();
  // Mode presse uniquement : une image externe réemployée sort de l'optimiseur Next (sinon 400). Forum inchangé.
  const isMediaMode = graphdebate?.mode === 'media';

  const hasMultiPhotos = useMemo(() => photos.length > 1, [photos]);

  const handleImageClick = (_event: MouseEvent, _content: any, index: number) => {
    setCurrentImageIndex(index);
    dialogRef.current?.showModal();
  };

  const closeLightbox = () => {
    dialogRef.current?.close();
  };

  const nextImage = () => {
    setCurrentImageIndex((currentImageIndex! + 1) % photos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((currentImageIndex + photos.length - 1) % photos.length);
  };

  // Mode presse : navigation clavier ←/→ dans le diaporama (Échap = fermeture native <dialog>).
  const handleLightboxKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (!isMediaMode || !hasMultiPhotos) return;
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  useEffect(() => {
    const currentDialog = dialogRef.current;

    if (currentDialog?.open) {
      const handleDialogCloseOnClickAway = (e: MouseEvent) =>
        currentDialog.open && isClickAwayFromElement(e, currentDialog) && currentDialog.close();

      currentDialog.addEventListener('click', e => handleDialogCloseOnClickAway(e));

      return currentDialog.removeEventListener('click', e => handleDialogCloseOnClickAway(e));
    }
  }, [dialogRef]);

  return (
    <div className="CustomGallery">
      <ReactSmartGallery
        images={photos}
        width="100%"
        height={isMobile() ? 250 : 400}
        onImageSelect={handleImageClick}
        rootStyle={{ maxWidth: '600px', margin: '0 auto' }}
      />
      <dialog
        ref={dialogRef}
        className="lightbox bg-transparent backdrop:bg-black/80 backdrop:backdrop-blur-sm"
        onKeyDown={handleLightboxKeyDown}
      >
        <button
          className="close fixed  top-5 right-5 text-white opacity-60 transition-all hover:opacity-100"
          style={{
            top: 'max(1.25rem, env(safe-area-inset-top))',
            right: 'max(1.25rem, env(safe-area-inset-right))',
          }}
          onClick={closeLightbox}
        >
          <IconClose className="h-8 w-8" />
        </button>
        <div className="image-container mx-auto relative flex items-center overflow-hidden max-w-[95vw] h-[80vh] md:max-w-[80vw] md:h-[80vh]">
          {hasMultiPhotos && (
            <div className="prev absolute left-0 bg-none h-full flex items-center justify-center z-10">
              <button
                className={
                  isMediaMode
                    ? 'ml-2 rounded-full bg-black/50 p-1.5 border-none text-white transition-all hover:bg-black/75 text-xl cursor-pointer'
                    : 'pl-2 border-none text-white opacity-60 transition-all hover:opacity-100 text-xl cursor-pointer'
                }
                onClick={prevImage}
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            </div>
          )}
          {hasMultiPhotos && (
            <div className="next absolute right-0 bg-none h-full flex items-center justify-center z-10">
              <button
                className={
                  isMediaMode
                    ? 'mr-2 rounded-full bg-black/50 p-1.5 border-none text-white transition-all hover:bg-black/75 text-xl cursor-pointer'
                    : 'pr-2 border-none text-white opacity-60 transition-all hover:opacity-100 text-xl cursor-pointer'
                }
                onClick={nextImage}
              >
                <ArrowRight className="h-6 w-6" />
              </button>
            </div>
          )}
          {isMediaMode && hasMultiPhotos && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-medium text-white">
              {currentImageIndex + 1} / {photos.length}
            </div>
          )}
          <Image
            src={photos[currentImageIndex]}
            unoptimized={isMediaMode && isExternalImageUrl(photos[currentImageIndex])}
            width={800}
            height={400}
            alt={`image ${currentImageIndex}`}
            className="max-w-full max-h-full object-center object-contain"
          />
        </div>
      </dialog>
    </div>
  );
};

export default CustomGallery;
